import { Dimensions, Platform, PixelRatio } from 'react-native';

// Base dimensions (iPhone 14 / iPhone 13 — the most common device)
const BASE_WIDTH = 390;
const BASE_HEIGHT = 844;

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

// Scale factor based on screen width (horizontal scaling)
const widthScale = SCREEN_WIDTH / BASE_WIDTH;
// Scale factor based on screen height (vertical scaling)
const heightScale = SCREEN_HEIGHT / BASE_HEIGHT;

/**
 * Scale a value based on screen width.
 * Use for: fontSize, padding, margin, icon sizes, border radius, etc.
 *
 * On iPhone 14 (390pt): scale(16) → 16
 * On iPhone SE (320pt): scale(16) → 13.1
 * On iPad 12.9" (1024pt): scale(16) → 42 (capped — see moderateScale)
 */
export function scale(size: number): number {
  return Math.round(PixelRatio.roundToNearestPixel(size * widthScale));
}

/**
 * Scale a value based on screen height.
 * Use for: vertical spacing, top/bottom margins, screen-height-dependent layouts.
 */
export function verticalScale(size: number): number {
  return Math.round(PixelRatio.roundToNearestPixel(size * heightScale));
}

/**
 * Moderate scale — scales less aggressively than scale().
 * Best for font sizes, padding, and margins where you want responsive
 * sizing but don't want things to get too huge on tablets.
 *
 * factor = 0.5 means "scale halfway between original and full scale"
 *   On iPhone SE: moderateScale(16) → 14.5 (slightly smaller)
 *   On iPhone 14: moderateScale(16) → 16 (unchanged)
 *   On iPad 12.9": moderateScale(16) → 29 (bigger but not 42)
 *
 * Use lower factor (0.3) for font sizes to prevent giant text on iPad.
 * Use higher factor (0.7) for spacing/padding where more scaling is fine.
 */
export function moderateScale(size: number, factor: number = 0.5): number {
  return Math.round(
    PixelRatio.roundToNearestPixel(size + (scale(size) - size) * factor)
  );
}

/**
 * Detect if device is a tablet (iPad or large Android).
 * Based on screen width — tablets are typically 600+ dp wide.
 */
export const isTablet = SCREEN_WIDTH >= 600;

/**
 * Detect if device is a small phone (iPhone SE, iPhone 8, etc.).
 * Screen width < 375 dp.
 */
export const isSmallPhone = SCREEN_WIDTH < 375;

/**
 * Get current screen dimensions (useful for dynamic layouts).
 */
export const screenWidth = SCREEN_WIDTH;
export const screenHeight = SCREEN_HEIGHT;

// Shorthand aliases for convenience
export const s = scale;
export const vs = verticalScale;
export const ms = moderateScale;
